
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { setMemberContext } from "@/utils/managerAuth";

export interface ProfileData {
  id: string;
  full_name: string;
  email: string;
  phone_number: string | null;
  profile_picture_url: string | null;
}

export const useProfileData = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);

  const fetchProfile = async () => {
    const memberId = sessionStorage.getItem('organization_member_id');
    console.log("Fetching profile with member ID:", memberId);
    
    if (!memberId) {
      console.log("No member ID found, redirecting to /manager");
      navigate('/manager');
      return;
    }

    try {
      const contextSet = await setMemberContext(memberId);
      if (!contextSet) {
        console.error('Failed to set member context');
        navigate('/manager');
        return;
      }

      const { count, error: countError } = await supabase
        .from('organization_members')
        .select('*', { count: 'exact', head: true })
        .eq('id', memberId);

      if (countError) throw countError;

      if (count === 0) {
        console.log("Member not found in database");
        sessionStorage.removeItem('organization_member_id');
        navigate('/manager');
        return;
      }

      const { data, error } = await supabase
        .from('organization_members')
        .select('id, full_name, email, phone_number, profile_picture_url')
        .eq('id', memberId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        console.log("No profile data found");
        sessionStorage.removeItem('organization_member_id');
        navigate('/manager');
        return;
      }

      console.log("Successfully fetched profile data:", data);
      setProfile(data);
    } catch (error) {
      console.error('Error in fetchProfile:', error);
      toast({
        title: "Error",
        description: "Failed to load profile data",
        variant: "destructive",
      });
      navigate('/manager');
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData: ProfileData) => {
    if (!profileData.id) return false;

    try {
      setSaving(true);
      console.log("Starting profile update for ID:", profileData.id);

      const contextSet = await setMemberContext(profileData.id);
      if (!contextSet) {
        console.error('Failed to set member context');
        return false;
      }

      const { data: updatedProfile, error: updateError } = await supabase
        .from('organization_members')
        .update({
          full_name: profileData.full_name,
          phone_number: profileData.phone_number,
          email: profileData.email,
        })
        .eq('id', profileData.id)
        .select()
        .maybeSingle();

      if (updateError) throw updateError;

      if (!updatedProfile) {
        throw new Error("Failed to update profile - no data returned");
      }

      setProfile(updatedProfile);
      console.log("Profile successfully updated:", updatedProfile);
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    profile,
    loading,
    saving,
    setProfile,
    fetchProfile,
    updateProfile,
  };
};
